// @ts-check

/**
 * @file Shared theme-aware text input. Wraps api-maker's slot-aware
 * `TextInput` with palette-driven background, border, and padding so
 * screens stop hand-rolling the same border/padding combo.
 */

import ApiMakerTextInput from "@kaspernj/api-maker/build/utils/text-input"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import PropTypes from "prop-types"
import {memo} from "react"
import {PanResponder, Platform} from "react-native"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useNow from "set-state-compare/build/use-now.js"

import {expoPublicEnvFlag} from "../shared/expo-public-env"
import {FORM_CONTROL_BORDER_RADIUS, FORM_CONTROL_BORDER_WIDTH, FORM_CONTROL_FONT_SIZE, FORM_CONTROL_LINE_HEIGHT, FORM_CONTROL_PADDING_HORIZONTAL, FORM_CONTROL_PADDING_VERTICAL} from "../shared/form-control-metrics"
import useThemeColors, {useThemeName} from "../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @import {GestureResponderEvent, PanResponderGestureState} from "react-native" */
/** @typedef {import("react-native").TextInput} NativeTextInput */
/** @type {Record<string, object>} */
const styles = {}
const MULTILINE_LINE_HEIGHT = 22
const MULTILINE_MIN_HEIGHT = 80
const MULTILINE_VERTICAL_EXTRA = 32

/**
 * @typedef {object} TextInputKeyboardEvent
 * @property {string} [key] Pressed key on React Native Web.
 * @property {{key?: string, text?: string, ctrlKey?: boolean, metaKey?: boolean, shiftKey?: boolean}} [nativeEvent] Native key event payload.
 * @property {boolean} [ctrlKey] Whether Ctrl was held on React Native Web.
 * @property {boolean} [metaKey] Whether Cmd/Meta was held on React Native Web.
 * @property {boolean} [shiftKey] Whether Shift was held on React Native Web.
 * @property {unknown} [target] Event target.
 * @property {unknown} [currentTarget] Current event target.
 * @property {() => void} [preventDefault] Prevents the textarea newline.
 * @property {() => boolean} [isDefaultPrevented] Whether default handling was already prevented.
 */

/** @typedef {import("react-native").NativeSyntheticEvent<import("react-native").TextInputFocusEventData>} TextInputFocusEvent */

/**
 * @typedef {{[key: string]: unknown, accessibilityLabel?: string, defaultValue?: string, editable?: boolean, inputRef?: RefObject<NativeTextInput | null>, multiline?: boolean, onBlur?: (event: TextInputFocusEvent) => unknown, onChangeText?: (value: string) => unknown, onContentSizeChange?: (event: {nativeEvent?: {contentSize?: {height?: number}}}) => unknown, onEndEditing?: (event: {nativeEvent?: {text?: string}}) => unknown, onFocus?: (event: TextInputFocusEvent) => unknown, onKeyPress?: (event: TextInputKeyboardEvent) => unknown, onSubmitEditing?: (event: {nativeEvent?: {text?: string}}) => unknown, secureTextEntry?: boolean, style?: object | object[], testID?: string, value?: string}} TextInputProps
 */

/**
 * @typedef {object} TextInputState
 * @property {number | null} autoHeight Automatically measured multiline height.
 * @property {number | null} manualHeight User-resized multiline height.
 */

/**
 * Theme-aware TextInput — palette `surface` background,
 * `borderStrong` outline, and shared single-line control metrics.
 * Accepts every prop api-maker `TextInput` accepts; the caller-supplied
 * `style` is merged on top of the themed base so one-off overrides still work.
 * @augments {ShapeComponent<TextInputProps, TextInputState>}
 */
class TextInput extends ShapeComponent {
  static propTypes = {
    accessibilityLabel: PropTypes.string,
    defaultValue: PropTypes.string,
    editable: PropTypes.bool,
    inputRef: PropTypes.object,
    multiline: PropTypes.bool,
    onBlur: PropTypes.func,
    onChangeText: PropTypes.func,
    onContentSizeChange: PropTypes.func,
    onEndEditing: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    onSubmitEditing: PropTypes.func,
    secureTextEntry: PropTypes.bool,
    testID: PropTypes.string,
    value: PropTypes.string,
    style: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
  }
  static defaultProps = {
    accessibilityLabel: undefined,
    defaultValue: undefined,
    editable: undefined,
    inputRef: undefined,
    multiline: false,
    onBlur: undefined,
    onChangeText: undefined,
    onContentSizeChange: undefined,
    onEndEditing: undefined,
    onFocus: undefined,
    onKeyPress: undefined,
    onSubmitEditing: undefined,
    secureTextEntry: undefined,
    style: undefined,
    testID: undefined,
    value: undefined
  }

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {object | object[]} */
  inputStyle = this.hookField()
  resizeStartHeight = MULTILINE_MIN_HEIGHT
  /** @type {ReturnType<typeof PanResponder.create> | null} */
  resizePanResponder = null
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {TextInputState} */
  state = {
    autoHeight: null,
    manualHeight: null
  }

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.resizePanResponder ||= PanResponder.create({
      onMoveShouldSetPanResponder: this.onMoveShouldSetResizeResponder,
      onPanResponderGrant: this.onResizeGrant,
      onPanResponderMove: this.onResizeMove,
      onStartShouldSetPanResponder: this.onStartShouldSetResizeResponder
    })
    this.themeName = useThemeName()
    const {defaultValue, multiline, style, value} = this.props
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const baseStyle = styles[`base-${themeName}-${Boolean(multiline)}`] ||= {
      backgroundColor: colors.surface,
      borderColor: colors.borderStrong,
      borderRadius: FORM_CONTROL_BORDER_RADIUS,
      borderWidth: FORM_CONTROL_BORDER_WIDTH,
      color: colors.text,
      fontSize: FORM_CONTROL_FONT_SIZE,
      lineHeight: multiline ? MULTILINE_LINE_HEIGHT : FORM_CONTROL_LINE_HEIGHT,
      minHeight: multiline ? 80 : undefined,
      paddingHorizontal: FORM_CONTROL_PADDING_HORIZONTAL,
      paddingRight: multiline ? 38 : FORM_CONTROL_PADDING_HORIZONTAL,
      paddingVertical: FORM_CONTROL_PADDING_VERTICAL
    }
    const textValue = value ?? defaultValue ?? ""
    const fallbackAutoHeight = multiline
      ? estimatedHeightForText(textValue)
      : undefined
    const height = multiline
      ? this.s.manualHeight || this.s.autoHeight || fallbackAutoHeight
      : undefined
    const heightStyle = multiline && height
      ? styles[`height-${Math.ceil(height)}`] ||= {
        height: Math.ceil(height)
      }
      : undefined
    const webFocusResetStyle = Platform.OS === "web"
      ? styles.webFocusReset ||= {
        outlineStyle: "none",
        outlineWidth: 0
      }
      : undefined

    useNow(() => {
      this.inputStyle = style || heightStyle || webFocusResetStyle
        ? [baseStyle, style, heightStyle, webFocusResetStyle]
        : baseStyle
    }, [baseStyle, style, heightStyle, webFocusResetStyle])
  }

  /**
   * Renders the themed text input.
   * @returns {ReactNode} Rendered input.
   * @throws {Error} When multiline rendering happens before setup initializes resize handling.
   */
  render() {
    // Destructure off `this.props` (not `this.p`) — `this.p` is a
    // fetching-object Proxy that doesn't implement `ownKeys`, so a
    // `{...rest}` spread on it silently captures no own keys and drops
    // every un-named prop (`testID`, `defaultValue`, `placeholder`, …).
    const {accessibilityLabel, defaultValue, inputRef, style, multiline, onSubmitEditing, secureTextEntry, testID, value, ...rest} = this.props
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const systemTestEnabled = expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST")
    const input = (
      <ApiMakerTextInput
        accessibilityLabel={accessibilityLabel}
        defaultValue={defaultValue}
        multiline={multiline}
        ref={this.tt.setInputRef}
        secureTextEntry={secureTextEntry && !systemTestEnabled}
        testID={testID}
        value={value}
        {...rest}
        onBlur={this.tt.onBlur}
        onChangeText={this.tt.onChangeText}
        onContentSizeChange={this.tt.onContentSizeChange}
        onFocus={this.tt.onFocus}
        onKeyPress={this.tt.onKeyPress}
        onSubmitEditing={onSubmitEditing}
        placeholderTextColor={colors.placeholder}
        selectionColor={colors.focusRing}
        style={this.tt.inputStyle}
      />
    )

    if (!multiline) return input
    const resizePanResponder = this.tt.resizePanResponder
    if (!resizePanResponder) throw new Error("Expected resize pan responder before rendering multiline TextInput")

    return (
      <View
        style={styles.resizeFrame ||= {
          position: "relative"
        }}
        testID={testID ? `${testID}/frame` : undefined}
      >
        {input}
        <View
          {...resizePanResponder.panHandlers}
          style={styles[`resizeHandle-${themeName}`] ||= {
            alignItems: "center",
            bottom: 4,
            height: 28,
            justifyContent: "center",
            position: "absolute",
            right: 4,
            width: 28
          }}
          testID={testID ? `${testID}/resizeHandle` : undefined}
        >
          <FontAwesome6
            color={colors.textMuted}
            name="up-right-and-down-left-from-center"
            size={12}
            testID={testID ? `${testID}/resizeHandle/icon` : undefined}
          />
        </View>
      </View>
    )
  }

  /**
   * Proposes an automatically measured multiline height.
   * @param {number} contentHeight Native measured content height.
   * @returns {void} Nothing.
   */
  applyAutoHeight(contentHeight) {
    if (!this.p.multiline || this.s.manualHeight != null) return
    if (!Number.isFinite(contentHeight) || contentHeight <= 0) return

    const nextHeight = Math.max(
      MULTILINE_MIN_HEIGHT,
      Math.ceil(contentHeight + MULTILINE_VERTICAL_EXTRA)
    )

    if (this.s.autoHeight == null || Math.abs(this.s.autoHeight - nextHeight) > 1) {
      this.s.autoHeight = nextHeight
    }
  }

  /**
   * Returns the current rendered multiline height before manual resize starts.
   * @returns {number} Current height in pixels.
   */
  currentResizeHeight() {
    const textValue = this.p.value ?? this.p.defaultValue ?? ""
    return this.s.manualHeight || this.s.autoHeight || estimatedHeightForText(textValue)
  }

  /**
   * Handles text input changes and keeps auto-height working on runtimes
   * that do not emit content-size changes for textareas.
   * @param {string} value Latest input contents.
   * @returns {void} Nothing.
   */
  onChangeText = (value) => {
    if (this.p.multiline && this.s.manualHeight == null) {
      this.applyAutoHeight(estimatedContentHeightForText(value))
    }

    if (this.p.onChangeText) {
      this.p.onChangeText(value)
    }
  }

  /**
   * Synthesizes `onEndEditing` on web, where React Native Web only emits blur.
   * @param {TextInputFocusEvent} event Focus event from the text input.
   * @returns {void} Nothing.
   */
  onBlur = (event) => {
    this.clearWebFocusStyle(event)
    if (this.p.onBlur) {
      this.p.onBlur(event)
    }
    if (Platform.OS !== "web") return
    if (this.p.onEndEditing) {
      this.p.onEndEditing({
        nativeEvent: {
          text: textValueFromFocusEvent(event)
        }
      })
    }
  }

  /**
   * Handles native content-size updates from multiline inputs.
   * @param {{nativeEvent?: {contentSize?: {height?: number}}}} event Native content-size event.
   * @returns {void} Nothing.
   */
  onContentSizeChange = (event) => {
    this.applyAutoHeight(event.nativeEvent?.contentSize?.height || 0)
    if (this.p.onContentSizeChange) {
      this.p.onContentSizeChange(event)
    }
  }

  /**
   * Applies focused styling and forwards the focus event.
   * @param {TextInputFocusEvent} event Focus event from the text input.
   * @returns {void} Nothing.
   */
  onFocus = (event) => {
    this.applyWebFocusStyle(event)
    if (this.p.onFocus) {
      this.p.onFocus(event)
    }
  }

  /**
   * Applies web focus border styling without rerendering uncontrolled inputs.
   * @param {TextInputFocusEvent} event Focus event from the text input.
   * @returns {void} Nothing.
   */
  applyWebFocusStyle(event) {
    if (Platform.OS !== "web") return

    const styleTarget = styleTargetFromFocusEvent(event)
    if (!styleTarget) return

    styleTarget.style.borderColor = this.tt.colors.focusRing
  }

  /**
   * Clears web focus border styling without rerendering uncontrolled inputs.
   * @param {TextInputFocusEvent} event Focus event from the text input.
   * @returns {void} Nothing.
   */
  clearWebFocusStyle(event) {
    if (Platform.OS !== "web") return

    const styleTarget = styleTargetFromFocusEvent(event)
    if (!styleTarget) return

    styleTarget.style.borderColor = ""
  }

  /**
   * Submits multiline inputs with Ctrl/Cmd+Enter while preserving plain newlines.
   * @param {TextInputKeyboardEvent} event Keyboard event from the text input.
   * @returns {void} Nothing.
   */
  onKeyPress = (event) => {
    if (this.p.onKeyPress) {
      this.p.onKeyPress(event)
    }
    if (!this.p.multiline || !this.p.onSubmitEditing) return
    if (event.isDefaultPrevented && event.isDefaultPrevented()) return
    if (!modifierEnterWasPressed(event)) return

    if (event.preventDefault) {
      event.preventDefault()
    }
    this.p.onSubmitEditing({
      nativeEvent: {
        text: textValueFromKeyboardEvent(event)
      }
    })
  }

  /**
   * Stores the rendered input and forwards it to callers.
   * @param {NativeTextInput | null} input Rendered native input.
   * @returns {void} Nothing.
   */
  setInputRef = (input) => {
    const {inputRef} = this.props
    if (inputRef) {
      inputRef.current = input
    }
  }

  /**
   * Allows the resize handle to claim touch/mouse starts.
   * @returns {boolean} True for multiline inputs.
   */
  onStartShouldSetResizeResponder = () => Boolean(this.p.multiline)

  /**
   * Allows the resize handle to claim touch/mouse movement.
   * @returns {boolean} True for multiline inputs.
   */
  onMoveShouldSetResizeResponder = () => Boolean(this.p.multiline)

  /**
   * Captures the current height when manual resizing begins.
   * @param {GestureResponderEvent} _event Responder event.
   * @param {PanResponderGestureState} _gestureState Gesture state.
   * @returns {void} Nothing.
   */
  onResizeGrant = (_event, _gestureState) => {
    this.resizeStartHeight = this.currentResizeHeight()
  }

  /**
   * Applies manual height changes from the resize handle drag.
   * @param {GestureResponderEvent} _event Responder event.
   * @param {PanResponderGestureState} gestureState PanResponder movement state.
   * @returns {void} Nothing.
   */
  onResizeMove = (_event, gestureState) => {
    if (!this.p.multiline) return

    this.s.manualHeight = Math.max(
      MULTILINE_MIN_HEIGHT,
      Math.ceil(this.resizeStartHeight + gestureState.dy)
    )
  }
}

/**
 * Estimates content height from explicit lines as a fallback for web
 * runtimes that do not emit native content-size events.
 * @param {string} value Input contents used for line counting.
 * @returns {number} Estimated content height in pixels.
 */
function estimatedContentHeightForText(value) {
  return Math.max(1, String(value).split("\n").length) * MULTILINE_LINE_HEIGHT
}

/**
 * Estimates rendered input height from explicit lines.
 * @param {string} value Input contents used for line counting.
 * @returns {number} Estimated rendered height in pixels.
 */
function estimatedHeightForText(value) {
  return Math.max(
    MULTILINE_MIN_HEIGHT,
    estimatedContentHeightForText(value) + MULTILINE_VERTICAL_EXTRA
  )
}

/**
 * Checks whether a keyboard event should submit a multiline field.
 * @param {TextInputKeyboardEvent} event Keyboard event from the text input.
 * @returns {boolean} Whether modifier Enter was pressed.
 */
function modifierEnterWasPressed(event) {
  const key = event.key || event.nativeEvent?.key
  const ctrlOrMetaPressed = Boolean(
    event.ctrlKey ||
    event.metaKey ||
    event.nativeEvent?.ctrlKey ||
    event.nativeEvent?.metaKey
  )
  const shiftPressed = Boolean(event.shiftKey || event.nativeEvent?.shiftKey)

  return key === "Enter" && ctrlOrMetaPressed && !shiftPressed
}

/**
 * Reads the current text value from a keyboard event.
 * @param {TextInputKeyboardEvent} event Keyboard event from the text input.
 * @returns {string} Current text value.
 */
function textValueFromKeyboardEvent(event) {
  return textValueFromEventTarget(event.target) ?? textValueFromEventTarget(event.currentTarget) ?? event.nativeEvent?.text ?? ""
}

/**
 * Reads the edited text from a native or web blur event.
 * @param {TextInputFocusEvent} event Focus event from the text input.
 * @returns {string} Current input value.
 */
function textValueFromFocusEvent(event) {
  const webEvent = /** @type {{currentTarget?: unknown, nativeEvent?: {target?: unknown, text?: unknown}, target?: unknown}} */ (
    /** @type {unknown} */ (event)
  )

  return textValueFromEventTarget(webEvent.currentTarget) ??
    textValueFromEventTarget(webEvent.target) ??
    textValueFromEventTarget(webEvent.nativeEvent?.target) ??
    (typeof webEvent.nativeEvent?.text === "string" ? webEvent.nativeEvent.text : "")
}

/**
 * Returns the web input element from a focus event when it exposes inline style.
 * @param {TextInputFocusEvent} event Focus event from the text input.
 * @returns {{style: {borderColor: string}} | null} Web style target.
 */
function styleTargetFromFocusEvent(event) {
  const webEvent = /** @type {{currentTarget?: unknown, nativeEvent?: {target?: unknown}, target?: unknown}} */ (
    /** @type {unknown} */ (event)
  )

  return styleTargetFromUnknown(webEvent.currentTarget) ??
    styleTargetFromUnknown(webEvent.target) ??
    styleTargetFromUnknown(webEvent.nativeEvent?.target)
}

/**
 * Narrows a possible web input element with inline style support.
 * @param {unknown} target Possible event target.
 * @returns {{style: {borderColor: string}} | null} Web style target.
 */
function styleTargetFromUnknown(target) {
  if (!target || typeof target !== "object" || !("style" in target)) return null

  const style = /** @type {{style?: unknown}} */ (target).style
  if (!style || typeof style !== "object" || !("borderColor" in style)) return null

  return /** @type {{style: {borderColor: string}}} */ (target)
}

/**
 * Reads textarea text from React Native Web keyboard event targets.
 * @param {unknown} target Keyboard event target.
 * @returns {string | null} Target value when present.
 */
function textValueFromEventTarget(target) {
  if (!target || typeof target !== "object" || !("value" in target)) return null

  const value = /** @type {{value?: unknown}} */ (target).value

  return typeof value === "string" ? value : null
}

/** @type {NamedExoticComponent<TextInputProps>} */
const TextInputComponent = memo(shapeComponent(TextInput))

export default TextInputComponent
